// Card — экземпляр карты на поле / в руке / в колоде / в сбросе.
// Создаётся из шаблона (template).
//
// Архитектура State Resolver (диздок 6.8.3):
//   card.base    — статы из данных, неизменны во время матча (atk/armor/hp)
//   card.current — текущие значения, меняются от урона / бафов / дебафов
// Геттеры atk / armor / hp / maxAtk / maxArmor / maxHp — для совместимости со старым кодом
// и удобства использования. Сеттеры atk/armor/hp пишут в current.

import { isHealerCard as isHealerCardId } from './abilities/global/healer-registry.js';
import { resolveInitiation } from './initiation.js';

let __nextInstanceId = 1;
export function nextInstanceId() {
  return __nextInstanceId++;
}

export class Card {
  constructor(template) {
    this.template = template;
    this.id = template.id;
    this.name_ru = template.name_ru;
    this.name_en = template.name_en ?? null;
    this.faction = template.faction;
    this.type = template.type;
    this.rarity = template.rarity;
    this.keywords = template.keywords ? [...template.keywords] : [];
    this.priority_override = template.priority_override ?? null;
    this.influence_cost = template.influence_cost ?? null;
    // Этап 21: если шаблон не задаёт initiation, применяется формула по rarity
    // (common=1, rare=3, epic=4, legendary=5). См. src/engine/initiation.js.
    this.initiation = resolveInitiation(template);
    this.deck_limit = template.deck_limit ?? null;
    this.ability_short = template.ability_short ?? '';
    this.ability_text = template.ability_text ?? {};
    this.flavor_ru = template.flavor_ru ?? '';

    // base — неизменные статы из шаблона
    this.base = {
      atk: template.atk ?? 0,
      armor: template.armor ?? 0,
      hp: template.hp ?? 0
    };
    // current — текущие значения
    this.current = {
      atk: this.base.atk,
      armor: this.base.armor,
      hp: this.base.hp
    };

    // Состояние на поле
    this.instanceId = nextInstanceId();
    this.ownerId = null;
    this.line = null;
    this.slot = null;
    this.placedTurn = null;
    this.revealed = false;
    this.activated = false;          // лог активации ключевых слов сработал хотя бы раз
    this.alive = true;
    this.heroAttackedThisTurn = false;

    // А.4.4.2+3: модификации.
    // persistentArmor — постоянная прибавка к броне (не сбрасывается в EOT).
    // tempArmor — временная прибавка с истечением: [{armor, expireAfterTurn}].
    // directorAuraApplied — текущий ауро-бонус от Вторых директоров (для пересчёта).
    this.persistentArmor = 0;
    this.persistentMaxArmor = 0;
    this.tempArmorBands = [];
    this.directorAuraApplied = { atk: 0, armor: 0, hp: 0 };
    // ИИ-ядро / Подпольный боец: passive «не может атаковать».
    // C.1: в v6 текст лежит в trigger/passive; ищем во всех полях.
    const allText = template?.ability_text
      ? Object.values(template.ability_text).filter(Boolean).join(' ')
      : '';
    this.cantAttack = /[Нн]е может атаковать/.test(allText)
      || template?.dsl_trigger1?.effect === 'cannot_attack_if_no_mod';
    // Карта Влияния, добавленная в руку через ИИ-ядро — играется бесплатно.
    this.freeCast = false;
    // А.4.4.4: на чьей стороне физически находится юнит.
    //   'owner'    — обычно: на стороне владельца.
    //   'opponent' — на стороне противника владельца (Спящий агент).
    this.side = 'owner';
    // А.4.4.4-fix1: состояние Спящего агента.
    //   'planted' — стоит у врага, ждёт активации в Ф4 владельца.
    //   'armed'   — активирован, в следующем Ф3 ударит ПЕРВЫМ и переедет к владельцу.
    //   null      — обычное состояние (не спящий или уже отработал).
    this.sleeperState = null;
    // А.4.5-fix4-retry2: применённый ауро-бонус Ликвидатора-эксперта (динамический,
    // пересчитывается при изменении состава поля). Хранится отдельно от persistentArmor /
    // directorAuraApplied, чтобы корректно вычислять delta при пересчёте.
    this.likvidatorEkspertBonus = 0;

    // Б.1.d: бонусная Мод-карта Сквилла в стартовой руке. Не замешивается в мулигане.
    this.skvillBonus = false;

    // А.5: счёт «контратаковал в этой Ф3». Сбрасывается в начале каждой Ф3.
    // Юнит, отдавший контратаку, теряет право на свою автоатаку до конца этой Ф3.
    // Контратаки на последующие атаки противника продолжают работать.
    this.counterAttackedThisPhase = false;

    // Б.1.b: Модификации (Синтетикс).
    // Каждый элемент — объект Mod (см. src/engine/abilities/syntetics/modifications.js).
    // Эффекты применяются дельтой к current/persistent при attach и откатываются при detach.
    this.modifications = [];
    // Порог Перегрузки: при modifications.length >= overloadThreshold юнит умрёт
    // в начале следующего хода. По умолчанию 3; пассивка Списанного дрона повышает до 4.
    this.overloadThreshold = 3;
    // Флаг «уже в Перегрузке» (для лога и для смерти в Ф1).
    this.overloaded = false;
    // Флаг «Скрытность была использована (после атаки в Ф4)»: блокирует повторное
    // получение keyword 'stealth' от Модов. Устанавливается activateStealthUnit.
    this.stealthUsed = false;
    // Б.2.d-fix1: явная инициализация флагов момента пробуждения для Синтетика-юнитов
    // (Личный телохранитель, Инициатор — onSpawn passive; Техно-палач, Фиксер — Order).
    // Без явного init проверки на `!card.spawnPassiveFired` работают по семантике
    // undefined→falsy, но явный false спокойнее для чтения и сериализации.
    this.spawnPassiveFired = false;
    this.orderFiredAtSpawn = false;

    // В.1.a: применённый бафф от Цепи командования. Дельта откатывается при
    // снятии (смерть источника, перемещение, Молчание, уход источника в сон).
    this.chainOfCommandApplied = { atk: 0, maxHp: 0 };
    // В.1.a: фракционный приоритет атаки у Глобала выбирается игроком при
    // выставлении юнита. Хранится тут (immutable после выбора). Для не-Глобала
    // не используется — selectAttackTarget берёт фракционный default.
    this.attackPriority = null;
    // В.1.b: ХИЛ-приоритет для is_healer-юнитов (Полевой медик и т.д.).
    // Выбирается игроком при выставлении (pick из HEAL_PRIORITIES). Используется
    // pickHealTarget в Order'е/Триггере хилера.
    this.healPriority = null;
    // В.1.c-1: keyword'ы, добавленные ЛИНЕЙНЫМИ ТРИГГЕРАМИ (Снайпер +pierce
    // на 2-й линии, Мех +taunt на 2-й, Расчёт +frenzy на 1-й и т.п.).
    // Снимаются при уходе с линии / смерти / Молчании. State Resolver
    // переcчитывает через recomputeLineTriggers.
    this.derivedKeywords = [];
    // В.1.c-1: keyword'ы от внешних Действий (Кустарный стимулятор → FRENZY
    // навсегда). Не Mod, не trigger по линии. Учитываются при удалении
    // derived/Mod, чтобы случайно не снять keyword, который пришёл из ещё
    // одного независимого источника.
    this.persistentKeywords = [];
    // В.1.c-2: флаг «Скрытность подавлена навсегда» — Нетраннер-адъютант
    // (Глобал) снимает stealth у всех вражеских юнитов до конца матча,
    // включая тех, у кого keyword идёт из base или Мода. Будущие попытки
    // Мода добавить stealth блокируются.
    this.stealthSuppressed = false;
    // В.1.d: список перманентных баффов «+atk/+armor/+hp до конца игры».
    // Применяются через src/engine/buffs.js (applyPermanentBuff). Откатываются
    // при возврате в руку Офицером связи через resetCardToTemplate.
    this.permanentBuffs = [];
    // В.1.f-1: «временные» keyword'ы — живут до конца указанного хода.
    // Используется Перегруппировкой (Глобал, frenzy на этот ход).
    // [{kw, sourceId, expireAfterTurn}]. Очищаются в endOfTurnCleanup.
    this.transientKeywords = [];
    // В.1.f-2-A: ВРЕМЕННЫЕ баффы со сроком жизни до начала указанного хода.
    // Применяются applyTemporaryBuff (Пропаганда Глобала: +1/1/1 на 2 хода).
    // Снимаются expireTemporaryBuffs в начале Ф1 хода expiresAtTurn.
    // [{id, label, atk, armor, hp, expiresAtTurn}].
    this.temporaryBuffs = [];
    // В.1.d: множество источников Молчания. Когда множество непустое —
    // card.current.silenced=true. Источники добавляются applySilence(state,
    // card, sourceId) и убираются removeSilence (для аур). Деструктивные
    // эффекты (снос Модов и keywords) применяются ОДИН РАЗ при первом
    // появлении источника; снятие источника лишь сбрасывает silenced-флаг.
    this.silenceSources = new Set();
    // В.1.a/В.1.b: помечает юнит-«хилер» (для атак-приоритета healer и для
    // hp picker'а у самого хилера). Источник: либо template.is_healer, либо
    // регистрация id в healer-registry (Полевой медик — регистрируется в
    // src/engine/abilities/global/polevoy_medik.js при импорте index.js).
    this.is_healer = template.is_healer === true || isHealerCardId(template.id);
    // В.1.a: флаг Молчания (диздок 6.7) — лишает юнит всех keyword'ов и текстовых
    // способностей до конца игры. См. src/engine/keywords/silence.js.
    if (this.current) this.current.silenced = false;
  }

  // === Геттеры/сеттеры текущих статов ===
  get atk()   { return this.current.atk; }
  set atk(v)  { this.current.atk = v; }
  get armor() { return this.current.armor; }
  set armor(v){ this.current.armor = v; }
  get hp()    { return this.current.hp; }
  set hp(v)   { this.current.hp = v; }

  // === Базовые (макс) значения ===
  get maxAtk()   { return this.base.atk; }
  get maxArmor() { return this.base.armor; }
  get maxHp()    { return this.base.hp; }

  // Удобно знать, отличается ли стат от базы (для UI с форматом "2/3").
  isStatModified(name) {
    return this.current[name] !== this.base[name];
  }

  isAlive() {
    return this.alive && this.hp > 0;
  }

  // Спит ли юнит на данном ходу матча.
  // initiation = минимальный номер хода, с которого юнит активен.
  isSleeping(currentTurn) {
    if (this.type !== 'unit') return false;
    // SANDBOX: режим без инициации — карта активна сразу (для теста
    // эпиков/легендарок с 1-го хода). Флаг ставится только в /sandbox,
    // в реальных боях (PvE/PvP) не выставляется.
    if (this._sandboxNoInit) return false;
    const init = this.initiation ?? 0;
    return currentTurn < init;
  }

  // Восстановить броню до «базы + постоянных бонусов + актуальных временных полос».
  // Вызывается в конце хода. tempArmorBands предварительно фильтруются в endOfTurnCleanup.
  restoreArmor() {
    const tempSum = this.tempArmorBands.reduce((s, b) => s + (b.armor || 0), 0);
    this.current.armor = this.base.armor + this.persistentArmor + tempSum + this.directorAuraApplied.armor;
  }

  // Максимум брони с учётом постоянных модификаций (для UI и для cap'а в баффах).
  get maxArmorWithMods() {
    return this.base.armor + this.persistentMaxArmor + this.directorAuraApplied.armor;
  }
}
